// LAP-550 V1.2 — Matrice de configuration des pages détail Marché
// Colonnes alignées sur les vrais champs renvoyés par /api/investment/market-view/
// (modèles StockEquity, BondInstrument, EtfAsset, CommodityMarket, RealEstateOpportunity,
//  AlternativeInvestment, CryptoAsset, StartupOpportunity, CurrencyExchange).
//
// LAP-551 — Les colonnes numériques passent par les fabriques de _shared/gridCols :
// la valeur brute reste triable/filtrable, seul l'affichage est formaté. Avant,
// le formatage se faisait dans `valueGetter` et le tableau triait des chaînes
// (« 1000 » avant « 99 »).
import {
  textCol,
  numberCol,
  moneyCol,
  compactCol,
  percentCol,
  deltaCol,
} from '../../_shared/gridCols'

// Colonnes StockEquity (réutilisées pour Séance / Europe / USA)
const STOCK_COLUMNS = [
  textCol({ field: 'name', headerName: 'Nom', minWidth: 180, flex: 1.4 }),
  textCol({ field: 'symbol', headerName: 'Symbole', width: 100 }),
  textCol({ field: 'sector', headerName: 'Secteur', minWidth: 130, flex: 1 }),
  numberCol({ field: 'price', headerName: 'Prix', width: 110 }),
  deltaCol({ field: 'variation_1d', headerName: 'Var 1J', width: 110 }),
  compactCol({ field: 'volume', headerName: 'Volume', width: 110 }),
  compactCol({ field: 'marketcap', headerName: 'Cap.', width: 110 }),
  numberCol({ field: 'per', headerName: 'PER', width: 80 }),
  numberCol({ field: 'score', headerName: 'Score', width: 80, decimals: 1 }),
]

const MARKET_CONFIG = {
  // ── Marché financier (5 sous-vues) ─────────────────────────────────
  financial: {
    title: 'investments.markeet.market.title',
    icon: '/images/download.svg',
    accent: '#60a5fa',
    sourceType: 'financial_market',
    // Marché volumineux (des milliers de titres) -> pagination côté serveur
    // (endpoint /market-detail/) pour ne jamais charger tout d'un coup.
    // Recherche, tri et filtres partent donc au serveur (cf. MarketDetail).
    paginated: true,
    subviews: [
      { value: 'financier', label: 'Séance', dataType: 'market_financier', columns: STOCK_COLUMNS },
      { value: 'europeen', label: 'Europe', dataType: 'market_europeen', columns: STOCK_COLUMNS },
      { value: 'americain', label: 'États-Unis', dataType: 'market_americain', columns: STOCK_COLUMNS },
      {
        value: 'obligation',
        label: 'Obligations',
        dataType: 'market_obligation',
        columns: [
          textCol({ field: 'name', headerName: 'Nom', minWidth: 180, flex: 1.4 }),
          textCol({ field: 'symbol', headerName: 'Symbole', width: 110 }),
          textCol({ field: 'emetteur', headerName: 'Émetteur', minWidth: 130, flex: 1 }),
          textCol({ field: 'notation', headerName: 'Notation', width: 100 }),
          numberCol({ field: 'price', headerName: 'Prix', width: 100 }),
          deltaCol({ field: 'variation_1_jour', headerName: 'Var 1J', width: 110 }),
          percentCol({ field: 'coupon', headerName: 'Coupon', width: 100 }),
          percentCol({ field: 'rendement', headerName: 'Rendement', width: 110 }),
          numberCol({ field: 'duration', headerName: 'Duration', width: 100 }),
        ],
      },
      {
        value: 'etf',
        label: 'ETF',
        dataType: 'market_etf',
        columns: [
          textCol({ field: 'name', headerName: 'Nom', minWidth: 180, flex: 1.4 }),
          textCol({ field: 'symbol', headerName: 'Symbole', width: 110 }),
          textCol({ field: 'sector', headerName: 'Secteur', minWidth: 130, flex: 1 }),
          textCol({ field: 'exchange', headerName: 'Bourse', width: 100 }),
          numberCol({ field: 'price', headerName: 'Prix', width: 100 }),
          deltaCol({ field: 'variation_1_jour', headerName: 'Var 1J', width: 110 }),
          deltaCol({ field: 'change_percentage', headerName: 'Var %', width: 100 }),
          compactCol({ field: 'volume', headerName: 'Volume', width: 110 }),
          compactCol({ field: 'marketcap', headerName: 'Cap.', width: 110 }),
        ],
      },
    ],
  },

  // ── Matières premières ─────────────────────────────────────────────
  commodities: {
    title: 'investments.markeet.commodities.title',
    icon: '/images/Commodities.png',
    accent: '#fbbf24',
    sourceType: 'commodity_market',
    dataPath: 'commodity_market',
    columns: [
      textCol({ field: 'name', headerName: 'Nom', minWidth: 180, flex: 1.2 }),
      textCol({ field: 'symbol', headerName: 'Symbole', width: 100 }),
      textCol({ field: 'type', headerName: 'Type', width: 120 }),
      numberCol({ field: 'price', headerName: 'Prix', width: 100 }),
      deltaCol({ field: 'variantion_1_jour', headerName: 'Var 1J', width: 110 }),
      compactCol({ field: 'volume', headerName: 'Volume', width: 110 }),
      numberCol({ field: 'yearLow', headerName: 'Plus bas 1A', width: 130 }),
      numberCol({ field: 'yearHigh', headerName: 'Plus haut 1A', width: 130 }),
      textCol({ field: 'exchange', headerName: 'Bourse', width: 110 }),
    ],
  },

  // ── Immobilier ─────────────────────────────────────────────────────
  realestate: {
    title: 'investments.markeet.realEstate.title',
    icon: '/images/Immobilier.png',
    accent: '#f97316',
    sourceType: 'real_estate_opportunity',
    columns: [
      textCol({ field: 'name', headerName: 'Bien', minWidth: 200, flex: 1.4 }),
      textCol({ field: 'type', headerName: 'Type', width: 110 }),
      textCol({ field: 'localisation', headerName: 'Localisation', minWidth: 130, flex: 1 }),
      numberCol({ field: 'superficie_m2', headerName: 'Surface (m²)', width: 120, decimals: 0 }),
      moneyCol({ field: 'price', headerName: 'Prix', width: 130 }),
      moneyCol({ field: 'prix_m2', headerName: '€/m²', width: 100 }),
      moneyCol({ field: 'loyer_mensuel', headerName: 'Loyer/mois', width: 130 }),
      deltaCol({ field: 'rendement', headerName: 'Rendement', width: 120 }),
      textCol({ field: 'recommandation', headerName: 'Reco', width: 110 }),
    ],
  },

  // ── Alternatifs (5 sous-vues) ──────────────────────────────────────
  alternative: {
    title: 'investments.markeet.alternative.title',
    icon: '/images/alternatif.png',
    accent: '#c084fc',
    sourceType: 'alternatifs',
    subviews: [
      {
        value: 'vins',
        label: 'Vins',
        dataType: 'vins_alternatifs',
        columns: [
          textCol({ field: 'name', headerName: 'Nom', minWidth: 200, flex: 1.4 }),
          textCol({ field: 'subtype', headerName: 'Type', width: 130 }),
          numberCol({ field: 'annee', headerName: 'Millésime', width: 110, decimals: 0 }),
          moneyCol({ field: 'price', headerName: 'Prix', width: 130 }),
          deltaCol({ field: 'variation_1_an', headerName: 'Var 1A', width: 110 }),
        ],
      },
      {
        value: 'montres',
        label: 'Montres',
        dataType: 'montre_alternatifs',
        columns: [
          textCol({ field: 'marque', headerName: 'Marque', width: 150 }),
          textCol({ field: 'modele', headerName: 'Modèle', minWidth: 180, flex: 1.4 }),
          textCol({ field: 'mouvement', headerName: 'Mouvement', width: 120 }),
          numberCol({ field: 'anneeProduction', headerName: 'Année', width: 100, decimals: 0 }),
          textCol({ field: 'etat', headerName: 'État', width: 100 }),
          moneyCol({ field: 'price', headerName: 'Prix', width: 130 }),
          deltaCol({ field: 'variation_1_an', headerName: 'Var 1A', width: 110 }),
        ],
      },
      {
        value: 'crypto',
        label: 'Crypto',
        dataType: 'crypto_asset',
        columns: [
          textCol({ field: 'symbol', headerName: 'Symbole', width: 110 }),
          textCol({ field: 'name', headerName: 'Nom', minWidth: 160, flex: 1.2 }),
          numberCol({ field: 'price', headerName: 'Prix', width: 130, decimals: 4 }),
          deltaCol({ field: 'changePercentage', headerName: 'Var 24h', width: 110 }),
          compactCol({ field: 'marketCap', headerName: 'Cap.', width: 130 }),
          compactCol({ field: 'volume', headerName: 'Volume', width: 130 }),
          numberCol({ field: 'yearHigh', headerName: 'Haut 1A', width: 120, decimals: 4 }),
          numberCol({ field: 'yearLow', headerName: 'Bas 1A', width: 120, decimals: 4 }),
        ],
      },
      {
        value: 'arts',
        label: 'Arts',
        dataType: 'art_alternatifs',
        columns: [
          textCol({ field: 'modele', headerName: 'Œuvre', minWidth: 200, flex: 1.4 }),
          textCol({ field: 'artiste', headerName: 'Artiste', minWidth: 150, flex: 1 }),
          textCol({ field: 'subtype', headerName: 'Type', width: 130 }),
          numberCol({ field: 'annee', headerName: 'Année', width: 100, decimals: 0 }),
          moneyCol({ field: 'price', headerName: 'Prix', width: 130 }),
        ],
      },
      {
        value: 'voitures',
        label: 'Voitures de collection',
        dataType: 'voiture_alternatifs',
        columns: [
          textCol({ field: 'marque', headerName: 'Marque', width: 140 }),
          textCol({ field: 'modele', headerName: 'Modèle', minWidth: 180, flex: 1.4 }),
          numberCol({ field: 'annee', headerName: 'Année', width: 100, decimals: 0 }),
          textCol({ field: 'etat', headerName: 'État', width: 110 }),
          moneyCol({ field: 'price', headerName: 'Prix', width: 130 }),
        ],
      },
    ],
  },

  // ── Startups ───────────────────────────────────────────────────────
  startups: {
    title: 'investments.markeet.startups.title',
    icon: '/images/Startup.png',
    accent: '#f472b6',
    sourceType: 'startup_capital',
    columns: [
      textCol({ field: 'name', headerName: 'Nom', minWidth: 180, flex: 1.4 }),
      textCol({ field: 'sector', headerName: 'Secteur', minWidth: 130, flex: 1 }),
      textCol({ field: 'localisation', headerName: 'Localisation', width: 150 }),
      textCol({ field: 'phase_dedeveloppement', headerName: 'Phase', width: 130 }),
      moneyCol({ field: 'price', headerName: 'Levée', width: 130 }),
      moneyCol({ field: 'valorisation_actuelle', headerName: 'Valorisation', width: 140 }),
      textCol({ field: 'investor', headerName: 'Investisseurs', width: 120 }),
      deltaCol({ field: 'croissance_ca_1_an', headerName: 'Croissance CA', width: 140 }),
    ],
  },

  // ── Forex / Devises ────────────────────────────────────────────────
  forex: {
    title: 'investments.markeet.forex.title',
    icon: '/images/Forex.png',
    accent: '#38bdf8',
    sourceType: 'currency_exchange',
    columns: [
      textCol({
        field: 'pair',
        headerName: 'Paire',
        minWidth: 130,
        flex: 1,
        valueGetter: (p) =>
          p.row.base_currency && p.row.target_currency
            ? `${p.row.base_currency} / ${p.row.target_currency}`
            : p.row.pair || '',
      }),
      numberCol({ field: 'exchange_rate', headerName: 'Taux', width: 130, decimals: 4 }),
      deltaCol({ field: 'variation_24h', headerName: 'Var 24h', width: 110 }),
      deltaCol({ field: 'variation_1y', headerName: 'Var 1A', width: 110 }),
      compactCol({ field: 'volume', headerName: 'Volume', width: 130 }),
    ],
  },
}

export default MARKET_CONFIG
